import { useEffect, useMemo, useState } from 'react';

import type { AxiosError } from 'axios';
import cn from 'classnames';
import CryptoSpinHoc from 'components/CryptoSpin/CryptoSpin';
import { Input } from 'components/form/Input/Input';
import type { IQRCodeOptions } from 'components/WidgetQrCode/WidgetQrCode';
import { WidgetQrCode } from 'components/WidgetQrCode/WidgetQrCode';
import { useSnackbar } from 'notistack';
import type { ChangeEvent, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import type { Currency } from 'services/model';
import type { IWidgetItem } from 'services/WidgetService/widget.model';
import { WidgetTypesEnum } from 'services/WidgetService/widget.model';
import type { ICalculateWidget } from 'services/WidgetService/widget.service';
import { WidgetService } from 'services/WidgetService/widget.service';
import { useDebounceSearch } from 'utils/hooks/useDebounceSearch';
import CurrencySelect from 'views/InvoicePay/CurrencySelect';

import { getWidgetColors } from './Widget.helper';
import WidgetError from './WidgetError';
import { WidgetHeader } from './WidgetHeader/WidgetHeader';

import { ReactComponent as IconCat } from 'assets/images/ui/icon-cat.svg';

import styles from './Widget.module.scss';

const options: IQRCodeOptions = {
  ecLevel: 'M',
  enableCORS: false,
  logoHeight: 35,
  logoOpacity: 1,
  logoPadding: 3,
  logoWidth: 35,
  size: 172,
};

export const Widget = () => {
  const [amount, setAmount] = useState<string>('');
  const debounceAmountValue = useDebounceSearch(amount, 500);
  const [currencySearch, setCurrencySearch] = useState<string>('');
  const debounceCurrencySearch = useDebounceSearch(currencySearch, 500);
  const [cashbackValue, setCashbackValue] = useState<string>('0.00');

  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null | undefined>(null);

  const [svgBase64, setSvgBase64] = useState('');

  const { id } = useParams();

  const getError = (error: AxiosError<any>): string[] | undefined => {
    if (error?.response?.data?.message) return [error?.response?.data?.message];

    if (error?.response?.data?.errors) return Object.values(error?.response?.data?.errors);
  };

  const widgetInfoQuery = useQuery(['widgetInfo'], () => WidgetService.getWidgetInfo(id ?? ''), {
    onSuccess: (data: IWidgetItem) => {
      const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="45" height="45" viewBox="0 0 91 104" fill="currentColor">
            <path d="M87.71 24.5805L48.2 1.77055C46.42 0.740547 44.2 0.740547 42.41 1.77055L2.9 24.5805C1.11 25.6105 0 27.5305 0 
              29.6005V75.2206C0 77.2806 1.11 79.2005 2.9 80.2405L42.41 103.051C43.29 103.561 44.29 103.821 45.31 103.821C46.33 103.821 
              47.33 103.551 48.21 103.051L87.72 80.2405C89.51 79.2105 90.62 77.2906 90.62 75.2206V29.6005C90.62 27.5405 89.51 
              25.6205 87.72 24.5805H87.71ZM46.2 49.5805C45.65 49.9005 44.96 49.9005 44.41 49.5805L6 27.4105L44.41 5.24055C44.95 
              4.93055 45.66 4.93055 46.2 5.24055L84.61 27.4105L46.2 49.5805V49.5805ZM42.41 53.0405C42.69 53.2005 42.99 53.3405 
              43.31 53.4505V98.9305L4.9 76.7705C4.35 76.4505 4 75.8606 4 75.2206V30.8705L42.41 53.0405V53.0405Z" 
              fill="${data?.customization?.qr_color}" 
            />
          </svg>`;

      const base64String = btoa(svgString);

      setSvgBase64(base64String);

      setCashbackValue(data?.cashback_amount ?? '0.00');
    },
    select: (data) => data.data?.result,
  });

  const getCurrencyListQuery = useQuery(
    ['currencyList', debounceCurrencySearch],
    () => WidgetService.getCurrencyList(id ?? '', debounceCurrencySearch),
    {
      enabled: !widgetInfoQuery?.data?.payment_currency && widgetInfoQuery.isSuccess,
      select: (data) =>
        data?.data?.result?.map((item) => ({
          currency: item.code,
          currency_icon: item.icon,
        })),
    },
  );

  const calculateWidgetMutation = useMutation(
    'calculateWidget',
    (data: ICalculateWidget) => WidgetService.calculateWidget(data),
    {
      onError: (error: AxiosError<any>) => {
        const errors = getError(error);

        errors &&
          enqueueSnackbar(errors[0], {
            content: (key) => (
              <WidgetError key={key} maxWight={widgetInfoQuery?.data?.customization?.width} message={errors[0]} />
            ),
            preventDuplicate: true,
            variant: 'error',
          });
      },
      onSuccess: (data) => {
        setCashbackValue(data?.data?.result?.cashback_amount ?? '0.00');
      },
    },
  );

  const createInvoiceMutation = useMutation(
    'createInvoice',
    (data: ICalculateWidget) => WidgetService.createInvoice(data),
    {
      onError: (error: AxiosError<any>) => {
        const errors = getError(error);

        errors &&
          enqueueSnackbar(errors[0], {
            content: (key) => (
              <WidgetError key={key} maxWight={widgetInfoQuery?.data?.customization?.width} message={errors[0]} />
            ),
            preventDuplicate: true,
            variant: 'error',
          });
      },
      onSuccess: (data) => {
        window.open(
          `https://pay.cryptomus.com/${data?.data?.result?.redirect.split('.com/')[1]}`, //TODO: NOT SPLIT
        );
      },
    },
  );

  const isValidBtn = () => {
    if (!selectedCurrency?.currency && !widgetInfoQuery?.data?.payment_currency) return false;

    if (!amount && !widgetInfoQuery?.data?.amount) return false;

    return true;
  };

  const handleClickSubmit = () => {
    createInvoiceMutation.mutate({
      amount: amount || widgetInfoQuery?.data?.amount,
      is_subtract: widgetInfoQuery?.data?.is_subtract,
      payment_currency: selectedCurrency?.currency ?? widgetInfoQuery?.data?.payment_currency,
      uuid: id ?? '',
    });
  };

  const handleCalcMutate = (calc_currency?: string) => {
    const currency = widgetInfoQuery?.data?.payment_currency ?? calc_currency ?? selectedCurrency?.currency;

    widgetInfoQuery?.data &&
      currency &&
      (debounceAmountValue || widgetInfoQuery?.data?.amount) &&
      calculateWidgetMutation.mutate({
        amount: debounceAmountValue || undefined,
        is_subtract: widgetInfoQuery?.data?.is_subtract,
        payment_currency: currency,
        uuid: id ?? '',
      });
  };

  //получаем цвета для виджета
  const widgetColors = useMemo(
    () =>
      getWidgetColors({
        actionBackground:
          widgetInfoQuery?.data?.customization?.button_color ?? widgetInfoQuery?.data?.customization?.qr_color,
        contentBackground: widgetInfoQuery?.data?.customization?.background_color,
        headerBackground: widgetInfoQuery?.data?.customization?.header_color,
      }),
    [widgetInfoQuery],
  );

  const getWidgetAction = () => {
    if (widgetInfoQuery?.data?.type === WidgetTypesEnum.QR) {
      return (
        <div className={styles.qrWrapper}>
          <WidgetQrCode
            url={`https://pay.cryptomus.com/widget/create-invoice/${id}`}
            options={{
              ...options,
              fgColor: widgetInfoQuery?.data?.customization?.qr_color,
              logoImage: `data:image/svg+xml;base64,${svgBase64}`,
            }}
          />
        </div>
      );
    }

    if (widgetInfoQuery?.data?.type === WidgetTypesEnum.BUTTON) {
      return (
        <button
          className={styles.button}
          style={{
            background: widgetInfoQuery?.data?.customization?.button_color,
            color: widgetColors.buttonTextColor,
          }}
          onClick={handleClickSubmit}
        >
          <span className={styles.buttonAmount}>
            {t('Pay')}{' '}
            {widgetInfoQuery?.data?.currency_type === 'fiat'
              ? `${widgetInfoQuery?.data?.payer_amount} ${widgetInfoQuery?.data?.payment_currency}`
              : `${widgetInfoQuery?.data?.amount} ${widgetInfoQuery?.data?.payment_currency}`}
          </span>

          <span className={styles.buttonCashback}>
            {t('Cashback')}
            <IconCat />+
            {amount
              ? (widgetInfoQuery?.data?.cashback_amount ??
                calculateWidgetMutation?.data?.data?.result?.cashback_amount ??
                '0.00')
              : '0.00'}{' '}
            CRMS
          </span>
        </button>
      );
    }

    return (
      <button
        className={styles.formButton}
        disabled={!isValidBtn()}
        style={{
          background: widgetInfoQuery?.data?.customization?.button_color,
          color: widgetColors.buttonTextColor,
        }}
        onClick={handleClickSubmit}
      >
        {t('Pay')} | {t('Cashback')}
        <IconCat />+{cashbackValue} CRMS
      </button>
    );
  };

  const handleChangeAmount = (e: ChangeEvent<HTMLInputElement>) => {
    if (/^\d{0,6}(\.\d{0,9})?$/.test(e.target.value) || e.target.value === '') {
      setAmount(e.target.value);
    }
  };

  const handleChangeCurrency = (item?: SetStateAction<Currency | null | undefined>) => {
    widgetInfoQuery?.data && item && handleCalcMutate((item as Currency).currency);
    setSelectedCurrency(item);
  };

  useEffect(() => {
    if (!debounceAmountValue) setCashbackValue('0.00');
    debounceAmountValue && handleCalcMutate();
  }, [debounceAmountValue]);

  return (
    <CryptoSpinHoc loading={widgetInfoQuery?.isLoading || getCurrencyListQuery?.isLoading}>
      <div className={styles.widget} style={{ width: widgetInfoQuery.data?.customization?.width }}>
        <WidgetHeader
          amount={amount}
          calculateWidget={calculateWidgetMutation?.data?.data?.result}
          cashbackValue={cashbackValue}
          isError={widgetInfoQuery.isError}
          selectedCurrency={selectedCurrency}
          widgetInfo={widgetInfoQuery?.data}
        />

        {widgetInfoQuery?.isError ? (
          <div className={styles.errorWrapper}>
            <p className={styles.statusCode}>404</p>
            <p className={styles.errorText}>{t('This widget is no longer available')}</p>
          </div>
        ) : (
          <div
            className={cn(styles.widgetContent, {
              [styles.noPadding]: widgetInfoQuery?.data?.type === WidgetTypesEnum.BUTTON,
            })}
            style={{
              backgroundColor: widgetInfoQuery.data?.customization?.background_color,
            }}
          >
            {!widgetInfoQuery?.data?.amount && (
              <Input
                className={styles.amountInput}
                placeholder={t('Enter amount')}
                value={amount}
                style={{
                  background: 'transparent',
                  border: `1px solid ${widgetColors.borderColors}`,
                  color: widgetColors.contentColors,
                }}
                onChange={handleChangeAmount}
              />
            )}

            {!widgetInfoQuery?.data?.payment_currency && (
              <div className={styles.currencySelectWrapper}>
                <CurrencySelect
                  isFiat
                  isWidget
                  openToTop
                  borderColor={widgetColors?.borderColors}
                  className={styles.currencySelect}
                  currencies={getCurrencyListQuery?.data ?? []}
                  inputCurrencyValueChange={currencySearch}
                  inputMethodValueChange=''
                  isInvoiceCurrenciesFetching={getCurrencyListQuery?.isLoading || getCurrencyListQuery?.isFetching}
                  selectedCurrency={selectedCurrency}
                  setSelectedCurrency={(item) => handleChangeCurrency(item)}
                  textColor={widgetColors.contentColors}
                  onInputCurrencyValueChange={setCurrencySearch}
                />
              </div>
            )}

            {getWidgetAction()}
          </div>
        )}
      </div>
    </CryptoSpinHoc>
  );
};
